import React from "react";
import { useTranslation } from "@helpers/translate";
import { useSessions } from "./hooks/useSessions";
import { getCountryFlag } from "./utils/constants";
import "./index.sass";

const Sessions = () => {
    const { __ } = useTranslation();
    const { sessions = [], loading } = useSessions();

    const getSessionValue = (session, keys, fallback = "-") => {
        for (const key of keys) {
            if (session[key]) {
                return session[key];
            }
        }

        return fallback;
    };

    const renderSessionRow = (session) => {
        const countryCode = getSessionValue(session, ["country_code", "countryCode"], "");
        const countryName = getSessionValue(session, ["country", "country_name", "countryName"]);
        const city = getSessionValue(session, ["city", "cityName"]);
        const flag = getCountryFlag(countryCode);

        return (
            <>
                <div className="sessions__cell sessions__cell--activity">
                    {getSessionValue(session, ["lastActivity", "last_activity"])}
                </div>

                <div className="sessions__cell sessions__cell--ip">
                    {getSessionValue(session, ["ip", "ip_address"])}
                </div>

                <div className="sessions__cell sessions__cell--device">
                    {getSessionValue(session, ["device"]) ||
                        `${session.device_name || ""} / ${session.os || ""}`.trim() ||
                        "-"}
                </div>

                <div className="sessions__cell sessions__cell--browser">
                    {getSessionValue(session, ["browser", "browser_name"])}
                </div>

                <div className="sessions__cell sessions__cell--country">
                    {flag && <span className="sessions__flag">{flag} </span>}
                    <span className="sessions__country-name">{countryName}</span>
                </div>

                <div className="sessions__cell sessions__cell--city">
                    {city}
                </div>
            </>
        );
    };

    const renderMobileSessionCard = (session) => {
        const countryCode = getSessionValue(session, ["country_code", "countryCode"], "");
        const countryName = getSessionValue(session, ["country", "country_name", "countryName"]);
        const city = getSessionValue(session, ["city", "cityName"]);
        const flag = getCountryFlag(countryCode);

        return (
            <div className="sessions__mobile-card">
                <div className="sessions__mobile-top">
                    <div className="sessions__mobile-block">
                        <div className="sessions__mobile-label">{__("settings.ip")}</div>
                        <div className="sessions__mobile-value">
                            {getSessionValue(session, ["ip", "ip_address"])}
                        </div>
                    </div>

                    <div className="sessions__mobile-block sessions__mobile-block--right">
                        <div className="sessions__mobile-label">
                            {__("settings.last_activity")}
                        </div>
                        <div className="sessions__mobile-value">
                            {getSessionValue(session, ["lastActivity", "last_activity"])}
                        </div>
                    </div>
                </div>

                <div className="sessions__mobile-bottom">
                    <div className="sessions__mobile-item">
                        <div className="sessions__mobile-label">{__("settings.device_os")}</div>
                        <div className="sessions__mobile-value">
                            {getSessionValue(session, ["device"]) ||
                                `${session.device_name || ""} / ${session.os || ""}`.trim() ||
                                "-"}
                        </div>
                    </div>

                    <div className="sessions__mobile-item">
                        <div className="sessions__mobile-label">{__("settings.browser")}</div>
                        <div className="sessions__mobile-value">
                            {getSessionValue(session, ["browser", "browser_name"])}
                        </div>
                    </div>

                    <div className="sessions__mobile-item">
                        <div className="sessions__mobile-label">{__("settings.country")}</div>
                        <div className="sessions__mobile-value sessions__mobile-value--country">
                            {flag && <span className="sessions__flag">{flag} </span>}
                            <span className="sessions__country-name">{countryName}</span>
                        </div>
                    </div>

                    <div className="sessions__mobile-item sessions__mobile-item--right">
                        <div className="sessions__mobile-label">{__("settings.city")}</div>
                        <div className="sessions__mobile-value">
                            {city}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="sessions sessions--settings">
            <h2 className="sessions__title">{__("settings.active_sessions")}</h2>

            <div className="sessions__table">
                <div className="sessions__header">
                    <div className="sessions__header-cell sessions__header-cell--activity">
                        {__("settings.last_activity")}
                    </div>

                    <div className="sessions__header-cell sessions__header-cell--ip">
                        {__("settings.ip")}
                    </div>

                    <div className="sessions__header-cell sessions__header-cell--device">
                        {__("settings.device_os")}
                    </div>

                    <div className="sessions__header-cell sessions__header-cell--browser">
                        {__("settings.browser")}
                    </div>

                    <div className="sessions__header-cell sessions__header-cell--country">
                        {__("settings.country")}
                    </div>

                    <div className="sessions__header-cell sessions__header-cell--city">
                        {__("settings.city")}
                    </div>
                </div>

                <div className="sessions__body">
                    {sessions.length > 0 ? (
                        sessions.map((session, index) => (
                            <div key={session.id || index} className="sessions__row">
                                {renderSessionRow(session)}
                            </div>
                        ))
                    ) : !loading ? (
                        <div className="sessions__row">
                            <div
                                className="sessions__cell"
                                style={{ gridColumn: "1 / -1" }}
                            >
                                {__("common.no_data") || "No active sessions"}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="sessions__table--mobile">
                {sessions.length > 0 ? (
                    sessions.map((session, index) => (
                        <div key={session.id || index}>
                            {renderMobileSessionCard(session)}
                        </div>
                    ))
                ) : !loading ? (
                    <div className="sessions__mobile-card">
                        <div className="sessions__mobile-value">
                            {__("common.no_data") || "No active sessions"}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default React.memo(Sessions);
